import { useCallback, useMemo, useState } from "react";
import { MenuItem, Select, Typography } from "@mui/material";
import {
  HarvestStockCounts,
  HarvestThemedCardValues,
  ReportThemedCardConfig,
  ReportValueCardProps,
} from "helpers/types";
import { SingleValueCardType } from "constants/index";
import {
  StrategyReportThemedCardDescriptions,
  StrategyReportThemedCardLabels,
  StrategyReportThemedCardValuesLabels,
} from "helpers/constants";
import { useStrategyReportValue } from "hooks/useReportStorage";
import { SELECT_STOCK_DISPLAY } from "../reportFieldStates";
import { useUncalculatedReportValue } from "../useUncalculatedReportValue";
import { STRATEGY_REPORT_DEFAULTS } from "./strategyReportDefaults";
import {
  SELLABLE_TODAY_PDF_LABEL,
  hasStock,
  sellableAsOfCaption,
} from "./harvestFigures";

/**
 * The Harvest Tax Benefits card: its six rows and the Bucket 3 stock picker
 * between two of them.
 *
 * Split, and the split is the whole point: Unrealized Gains/Losses IS
 * calculated -- `_resolve_unrealized_gl` is the one figure of Harvest the
 * bucket report produces today -- so it keeps the localStorage hook every
 * other calculated row uses, and a run replaces whatever is under it. Shares
 * Sold and Sellable Today are calculated PER BUCKET 3 STOCK and read through
 * the picker. The other three reach no calculation at all. Those five are
 * session-scoped for the reason useUncalculatedReportValue states.
 */
export const useHarvestCard = (reportScopeKey: string) => {
  const [realizedLosses, setRealizedLosses] = useUncalculatedReportValue(
    "realized_losses_strategy",
    STRATEGY_REPORT_DEFAULTS.REALIZED_LOSSES,
    reportScopeKey,
  );
  const [lossesUsed, setLossesUsed] = useUncalculatedReportValue(
    "losses_used_strategy",
    STRATEGY_REPORT_DEFAULTS.LOSSES_USED,
    reportScopeKey,
  );
  const [poolRemaining, setPoolRemaining] = useUncalculatedReportValue(
    "pool_remaining_strategy",
    STRATEGY_REPORT_DEFAULTS.POOL_REMAINING,
    reportScopeKey,
  );
  const [unrealizedGains, setUnrealizedGains] = useStrategyReportValue(
    "unrealized_gains_strategy",
    STRATEGY_REPORT_DEFAULTS.UNREALIZED_GAINS,
  );
  // Component state, not storage, and deliberately: the reason belongs to
  // the RUN, while the figure it explains is persisted. A reload or an
  // account switch must not resurrect a refusal from a run that is gone --
  // the same split the Keep card's rows make (keepRowFigure).
  const [unrealizedGainsUnavailable, setUnrealizedGainsUnavailable] = useState<
    string | undefined
  >(undefined);
  const [sharesSold, setSharesSold] = useUncalculatedReportValue(
    "shares_sold_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_SOLD,
    reportScopeKey,
  );
  const [sellableToday, setSellableToday] = useUncalculatedReportValue(
    "sellable_today_strategy",
    STRATEGY_REPORT_DEFAULTS.SELLABLE_TODAY,
    reportScopeKey,
  );

  // The per-stock share counts, and which stock is showing.
  //
  // Component state, never written through the value hooks above, on the
  // Keep card's terms: the map belongs to the run that produced it, and a
  // map left standing would credit one period's counts to whatever period
  // is on screen. The PICK is component state for the same reason -- the
  // symbols come from the run, so a remembered symbol a new run does not
  // carry means nothing.
  //
  // The figures themselves DO go through the two hooks above rather than
  // into state, because those rows stay hand-editable: an operator's own
  // number waits underneath a refusal instead of being destroyed by it,
  // which is the rule every editable row on these cards follows.
  const [harvestStocks, setHarvestStocks] = useState<Record<
    string,
    HarvestStockCounts
  > | null>(null);
  const [selectedHarvestStock, setSelectedHarvestStock] = useState("");
  // Whether each row is waiting on a PICK rather than on a report. Held
  // apart from the figures, and never written through the value hooks,
  // exactly as the Keep card's row states are: writing the sentinel into
  // the holder would overwrite whatever an operator had typed into these
  // rows, and it would then read back as the sentinel.
  //
  // ONE FLAG PER ROW, not one for the pair: they are edited independently,
  // and a shared flag would mean typing into Shares Sold took Sellable
  // Today out of "Select stock" too -- revealing whatever figure had been
  // hand-typed into it, as if this run had produced it.
  const [sharesSoldPending, setSharesSoldPending] = useState(false);
  const [sellableTodayPending, setSellableTodayPending] = useState(false);
  // Whether the Sellable Today figure on screen is the BACKEND's, which is
  // what entitles it to the snapshot-date caption beside it. A hand-typed
  // figure is the reader's own, and a date asserting Orion measured it
  // would be false -- on the card and, through pdfLabel, on the client's
  // PDF. Tracked rather than inferred from the pending flag, because that
  // flag is cleared by a typed edit as well as by a pick.
  const [sellableTodayFromBackend, setSellableTodayFromBackend] =
    useState(false);
  const [sharesSoldReason, setSharesSoldReason] = useState<string | undefined>(
    undefined,
  );
  const [sellableTodayReason, setSellableTodayReason] = useState<
    string | undefined
  >(undefined);
  // The day the sellable counts were read on -- a companion to them, and
  // outside the pick because every stock's count shares it.
  const [sellableAsOf, setSellableAsOf] = useState<string | null>(null);

  // The two rows back to "no stock picked": waiting on a pick when there is
  // one to make, and carrying no backend date or reason either way.
  const clearStockPick = useCallback((pending: boolean) => {
    setSharesSoldPending(pending);
    setSellableTodayPending(pending);
    setSellableTodayFromBackend(false);
    setSharesSoldReason(undefined);
    setSellableTodayReason(undefined);
  }, []);

  const applyHarvestStock = useCallback(
    (stocks: Record<string, HarvestStockCounts>, symbol: string) => {
      // The own-property read makes the safety independent of the
      // `typeof === "number"` guards below, which already reject what a
      // prototype lookup returns, rather than fixing a live defect.
      if (!hasStock(stocks, symbol)) return;
      const counts = stocks[symbol];
      if (!counts) return;
      setSharesSoldPending(false);
      setSellableTodayPending(false);
      // ONLY a real figure is stored, and the reason is what empties the
      // row otherwise -- the rule the Unrealized G/L row follows, and for
      // the same reason: writing a null through these setters would
      // destroy a hand-entered number rather than merely hide it.
      if (typeof counts.shares_sold === "number") {
        setSharesSold(counts.shares_sold);
      }
      if (typeof counts.sellable_today === "number") {
        setSellableToday(counts.sellable_today);
      }
      // The caption follows the FIGURE: a refused count has no day to name
      // either, so this is false exactly when there is no backend number
      // on the row.
      setSellableTodayFromBackend(typeof counts.sellable_today === "number");
      // Re-read on every switch: each stock is refused on its own terms,
      // so a reason left standing from the previous ticker would explain
      // this one's blank with another one's cause.
      setSharesSoldReason(counts.unavailable?.shares_sold);
      setSellableTodayReason(counts.unavailable?.sellable_today);
    },
    [setSharesSold, setSellableToday],
  );

  const handleHarvestStockChange = useCallback(
    (symbol: string) => {
      setSelectedHarvestStock(symbol);
      if (harvestStocks) applyHarvestStock(harvestStocks, symbol);
    },
    [harvestStocks, applyHarvestStock],
  );

  // Typing over either row is the reader supplying the figure, so the
  // backend's reason no longer applies and the row stops waiting on a pick
  // -- the same rule as every other hand-editable row on these cards.
  const handleSharesSoldChange = useCallback(
    (value: number | string) => {
      setSharesSoldPending(false);
      setSharesSoldReason(undefined);
      setSharesSold(value);
    },
    [setSharesSold],
  );

  const handleSellableTodayChange = useCallback(
    (value: number | string) => {
      setSellableTodayPending(false);
      setSellableTodayReason(undefined);
      // From here the figure is the reader's own, so the snapshot date no
      // longer describes it.
      setSellableTodayFromBackend(false);
      setSellableToday(value);
    },
    [setSellableToday],
  );

  // Applies a completed run's Harvest payload.
  const applyRun = useCallback(
    (harvest: HarvestThemedCardValues | undefined) => {
      // Account-wide, so it survives a run that carried no Bucket 1 data.
      // A null is NOT applied over the stored value -- a refusal must not
      // destroy a hand-entered figure -- but it is not left standing in
      // silence either: the reason goes into component state and the row
      // renders N/A behind it, so a stored number cannot pass for this
      // run's result. `!= null` and not `!`, so a genuine net-zero total
      // is applied rather than swallowed as falsy. The figure is null when
      // the Bucket 3 account has no position snapshot.
      if (harvest?.unrealized_gain_loss != null) {
        setUnrealizedGains(harvest.unrealized_gain_loss);
      }
      setUnrealizedGainsUnavailable(harvest?.unavailable ?? undefined);
      // The per-stock counts, and the pick they are read with.
      //
      // `stocks` is `{}` when the run named no Bucket 3 or the bucket has
      // no members, which is an ANSWER and not an absence -- so the empty
      // map is stored, the select has nothing to offer and is not
      // rendered, and the two rows stay on their seed. A result cached
      // before these rows shipped carries no `stocks` key at all, and THAT
      // is the absence: it leaves the rows exactly as they were.
      const stocks = harvest?.stocks ?? null;
      setHarvestStocks(stocks);
      setSellableAsOf(harvest?.sellable_as_of ?? null);
      // Keep the previously picked stock when this run still carries it --
      // the Keep card's rule, and what makes re-running a report for a new
      // period not throw away where the reader was looking.
      const pick =
        selectedHarvestStock && stocks && hasStock(stocks, selectedHarvestStock)
          ? selectedHarvestStock
          : "";
      setSelectedHarvestStock(pick);
      if (pick && stocks) {
        applyHarvestStock(stocks, pick);
      } else {
        // Waiting on a pick, and ONLY when there is something to pick: a
        // run that carried no stocks at all has nothing for the reader to
        // do, so telling them to select a stock would be a dead end.
        clearStockPick(Object.keys(stocks ?? {}).length > 0);
      }
    },
    [
      setUnrealizedGains,
      selectedHarvestStock,
      applyHarvestStock,
      clearStockPick,
    ],
  );

  // A change of report inputs retires the run on screen, and every piece of
  // the picker's state has to go together: the stocks belong to the run that
  // produced them, so a map left standing offers the PREVIOUS client's
  // tickers, the counts beside it read as this client's, and the caption
  // dates them to a snapshot of the other account's. The two figures
  // themselves are scope-keyed by their own hook and return to their seed on
  // their own.
  const resetForNewInputs = useCallback(() => {
    setHarvestStocks(null);
    setSelectedHarvestStock("");
    clearStockPick(false);
    setSellableAsOf(null);
  }, [clearStockPick]);

  const sellableAsOfText = useMemo(
    () => sellableAsOfCaption(sellableAsOf),
    [sellableAsOf],
  );

  // The stocks this run offers. Already alphabetical -- the bucket's
  // selection is ordered by symbol and that order survives to the
  // response's object keys.
  const harvestStockOptions = useMemo(
    () => Object.keys(harvestStocks ?? {}),
    [harvestStocks],
  );

  const rows = useMemo((): ReportValueCardProps[] => {
    // The day the count was taken, beside the figure. It appears only with
    // a BACKEND count: a date beside the "Select stock" placeholder would
    // describe a figure nobody has asked for yet, and beside a hand-typed
    // one it would credit the reader's own number to an Orion snapshot.
    const sellableCaption =
      sellableAsOfText && sellableTodayFromBackend ? sellableAsOfText : null;
    return [
      {
        label: StrategyReportThemedCardValuesLabels.REALIZED_LOSSES,
        value: realizedLosses,
        type: SingleValueCardType.CURRENCY,
        onValueChange: setRealizedLosses,
      },
      {
        label: StrategyReportThemedCardValuesLabels.LOSSES_USED,
        value: lossesUsed,
        type: SingleValueCardType.CURRENCY,
        onValueChange: setLossesUsed,
      },
      {
        label: StrategyReportThemedCardValuesLabels.POOL_REMAINING,
        value: poolRemaining,
        type: SingleValueCardType.CURRENCY,
        onValueChange: setPoolRemaining,
      },
      {
        label: StrategyReportThemedCardValuesLabels.UNREALIZED_GAINS,
        // The empty sentinel WITH the reason is what renders a muted "N/A"
        // behind an info icon and what drops the row from the client-facing
        // PDF. The stored figure waits underneath it, so a refusal costs the
        // reader nothing but the false claim that it is this run's answer.
        value: unrealizedGainsUnavailable ? "" : unrealizedGains,
        unavailableReason: unrealizedGainsUnavailable,
        type: SingleValueCardType.CURRENCY,
        onValueChange: setUnrealizedGains,
      },
      {
        label: StrategyReportThemedCardValuesLabels.SHARES_SOLD,
        // Three states, read in the order the card resolves them. A REFUSAL
        // outranks the rest and empties the row -- the Unrealized G/L row's
        // rule, and MultiValuesCard's: the reason renders N/A behind an info
        // icon, and the stored figure waits underneath rather than passing
        // for this stock's count. Then "waiting on a pick", which only a run
        // carrying stocks can be in. The seed underneath is reached only
        // when this run carried no stocks to pick from at all.
        value: sharesSoldReason
          ? ""
          : sharesSoldPending
            ? SELECT_STOCK_DISPLAY
            : sharesSold,
        type: SingleValueCardType.NUMBER,
        onValueChange: handleSharesSoldChange,
        unavailableReason: sharesSoldReason,
      },
      // The select the two rows around it are read with, on its own row
      // BETWEEN them because that is where the operator asked for it (task
      // 3145) -- and it reads as the thing driving both, which it would not
      // from either row's own value slot.
      //
      // PRESENT ONLY WHEN THERE IS SOMETHING TO PICK. Before any run, and
      // for a bucket with no members, an empty select is a control offering
      // an action that cannot be taken -- beside two rows already saying "No
      // Report", which is the honest account of that state on its own. The
      // Keep card's header picker appears on the same condition.
      ...(harvestStockOptions.length === 0
        ? []
        : [
            {
              label: StrategyReportThemedCardValuesLabels.BUCKET_3_STOCK,
              // No figure here at all; `control` is what renders, and the
              // row is absent from the PDF export. The value is required by
              // the row type and never read.
              value: "",
              type: SingleValueCardType.NUMBER,
              onValueChange: () => {},
              // FULL WIDTH, and that is what puts it BETWEEN the two rows
              // rather than beside one of them. The card is a two-column
              // grid, so a half-width row here would land in the right-hand
              // cell of the Shares Sold row and push Sellable Today onto the
              // next line -- which reads as a select belonging to Shares
              // Sold alone, the opposite of what it does. Taking the whole
              // width forces its own line and leaves the two figures stacked
              // above and below it, which is the arrangement the operator
              // asked for and the only one in which the control visibly
              // governs both.
              fullWidthRow: true,
              control: (
                <Select
                  size="small"
                  displayEmpty
                  value={selectedHarvestStock}
                  onChange={(event) =>
                    handleHarvestStockChange(event.target.value)
                  }
                  inputProps={{
                    "aria-label":
                      StrategyReportThemedCardValuesLabels.BUCKET_3_STOCK,
                  }}
                  // Wide enough for a five-character ticker and for the
                  // placeholder that stands in for one, so the control does
                  // not resize as the reader moves between stocks. Small
                  // enough to leave the row it occupies uncrowded.
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="" disabled>
                    {SELECT_STOCK_DISPLAY}
                  </MenuItem>
                  {harvestStockOptions.map((symbol) => (
                    <MenuItem key={symbol} value={symbol}>
                      {symbol}
                    </MenuItem>
                  ))}
                </Select>
              ),
            },
          ]),
      {
        label: StrategyReportThemedCardValuesLabels.SELLABLE_TODAY,
        // Same three states, same order, same reason -- see Shares Sold.
        value: sellableTodayReason
          ? ""
          : sellableTodayPending
            ? SELECT_STOCK_DISPLAY
            : sellableToday,
        type: SingleValueCardType.NUMBER,
        onValueChange: handleSellableTodayChange,
        unavailableReason: sellableTodayReason,
        action: sellableCaption ? (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
          >
            {sellableCaption}
          </Typography>
        ) : undefined,
        // The PDF has no place for the caption, and "Sellable Today" on
        // paper with no date is the one claim on this card a reader cannot
        // check.
        pdfLabel: sellableCaption
          ? `${SELLABLE_TODAY_PDF_LABEL} (${sellableCaption})`
          : undefined,
      },
    ];
  }, [
    realizedLosses,
    lossesUsed,
    poolRemaining,
    unrealizedGains,
    unrealizedGainsUnavailable,
    sharesSold,
    sharesSoldPending,
    sharesSoldReason,
    sellableToday,
    sellableTodayPending,
    sellableTodayReason,
    sellableTodayFromBackend,
    sellableAsOfText,
    harvestStockOptions,
    selectedHarvestStock,
    setRealizedLosses,
    setLossesUsed,
    setPoolRemaining,
    setUnrealizedGains,
    handleSharesSoldChange,
    handleSellableTodayChange,
    handleHarvestStockChange,
  ]);

  const card = useMemo(
    (): ReportThemedCardConfig => ({
      label: StrategyReportThemedCardLabels.HARVEST,
      description: StrategyReportThemedCardDescriptions.HARVEST,
      values: rows,
      addictional_component: null,
    }),
    [rows],
  );

  return { card, applyRun, resetForNewInputs };
};
